import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, input, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isHex, normalizeHex } from '../../../core/utils/color.util';

/** Campo de color: muestra (abre selector nativo) + hex editable + cuentagotas + paleta sugerida. */
@Component({
  selector: 'app-color-field',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ColorFieldComponent), multi: true }],
  template: `
    @if (!paletteOnly()) {
      <div class="field" [class.is-invalid]="invalid()">
        <!-- El input nativo cubre la muestra: el toque cae directo sobre él (necesario en iOS Safari). -->
        <span class="sw" [style.background]="value()">
          <input #native type="color" class="native" aria-label="Elegir color" [value]="value()"
            (input)="set($any($event.target).value)" (change)="set($any($event.target).value)" />
        </span>
        <input class="hex" [value]="draft()" maxlength="7" spellcheck="false" [attr.aria-label]="label() || 'Color'"
          (input)="onDraft($any($event.target).value)" (blur)="commitDraft()" (keydown.enter)="commitDraft()" />
        <button type="button" class="drop" aria-label="Cuentagotas" (click)="pick()"><cr-icon name="eyedropper" [size]="18" /></button>
      </div>
    }
    <div class="palette" role="radiogroup" [attr.aria-label]="label() || 'Colores sugeridos'">
      @for (c of palette(); track c) {
        <button type="button" class="dot" role="radio" [attr.aria-checked]="c === value()" [class.is-on]="c === value()"
          [style.background]="c" [attr.aria-label]="c" (click)="set(c)"></button>
      }
      @if (paletteOnly()) {
        <!-- "Otro color": el input nativo cubre todo el botón para que el toque lo abra en iOS/Android/escritorio. -->
        <span class="dot other" title="Otro color">
          <cr-icon name="eyedropper" [size]="16" aria-hidden="true" />
          <input #native type="color" class="native" aria-label="Elegir color" [value]="value()"
            (input)="set($any($event.target).value)" (change)="set($any($event.target).value)" />
        </span>
        <span class="hex-label">{{ value() }}</span>
      }
    </div>
  `,
  styles: [`
    :host{display:flex;flex-direction:column;gap:12px;min-width:0}
    .field{position:relative;display:flex;align-items:center;gap:12px;height:46px;padding:0 6px 0 10px;border:1.2px solid var(--cr-border-subtle);border-radius:12px;background:#fff}
    .field:focus-within{border:2px solid var(--cr-brand-teal)}
    .field.is-invalid{border:2px solid var(--cr-danger)}
    .sw{position:relative;display:block;width:26px;height:26px;padding:0;border:0;border-radius:7px;box-shadow:inset 0 0 0 1px rgb(0 0 0 / .1);flex:none;cursor:pointer;transition:transform .12s ease, box-shadow .12s ease}
    .sw:hover{transform:scale(1.08);box-shadow:inset 0 0 0 1px rgb(0 0 0 / .1), 0 0 0 3px var(--cr-bg-surface-2)}
    .sw:has(.native:focus-visible), .dot.other:has(.native:focus-visible){outline:2px solid var(--cr-brand-teal);outline-offset:2px}
    .hex{flex:1;min-width:0;border:0;outline:0;font:600 15px/22px var(--cr-font-body);text-transform:uppercase;background:transparent}
    .drop{width:34px;height:34px;border:0;border-radius:10px;background:transparent;display:grid;place-items:center;cursor:pointer;color:var(--cr-text-primary)}
    .drop:hover{background:var(--cr-bg-surface-2)}
    /* Input nativo invisible pero tocable, encima de la muestra / botón "Otro color" (iOS no abre el selector por click programático). */
    .native{position:absolute;inset:0;z-index:1;display:block;width:100%;height:100%;min-width:0;margin:0;padding:0;border:0;border-radius:inherit;background:transparent;opacity:0;cursor:pointer;pointer-events:auto;-webkit-appearance:none;appearance:none}
    .palette{position:relative;display:flex;flex-wrap:wrap;gap:8px;align-items:center}
    .dot{width:34px;height:34px;border-radius:50%;border:0;cursor:pointer;box-shadow:inset 0 0 0 1px rgb(0 0 0 / .12);position:relative;flex:none}
    .dot.is-on{box-shadow:0 0 0 2px #fff, 0 0 0 4px var(--cr-brand-teal)}
    .dot.other{background:var(--cr-bg-surface-2);display:grid;place-items:center;color:var(--cr-text-primary)}
    .hex-label{font:600 14px/18px var(--cr-font-body);margin-left:4px;text-transform:uppercase}
  `],
})
export class ColorFieldComponent implements ControlValueAccessor {
  readonly palette = input<string[]>([]);
  readonly label = input<string>('');
  readonly paletteOnly = input<boolean>(false);
  readonly value = signal('#FFFFFF');
  readonly draft = signal('#FFFFFF');
  readonly invalid = signal(false);
  private native = viewChild<ElementRef<HTMLInputElement>>('native');

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  set(v: string): void {
    const hex = normalizeHex(v);
    if (!isHex(hex)) return;
    this.value.set(hex);
    this.draft.set(hex);
    this.invalid.set(false);
    this.onChange(hex);
    this.onTouched();
  }

  onDraft(v: string): void {
    this.draft.set(v);
    const hex = normalizeHex(v);
    if (isHex(hex)) this.set(hex);
  }

  commitDraft(): void {
    const hex = normalizeHex(this.draft());
    if (isHex(hex)) this.set(hex);
    else { this.invalid.set(true); this.draft.set(this.value()); setTimeout(() => this.invalid.set(false), 1200); }
  }

  /**
   * Abre el selector de color nativo por código (solo fallback del cuentagotas sin EyeDropper).
   * La muestra y "Otro color" no lo usan: el input nativo los cubre y recibe el toque directamente.
   */
  openPicker(): void {
    const el = this.native()?.nativeElement;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* sin soporte o sin activación → click */ }
    }
    el.click();
  }

  /** Cuentagotas de pantalla si el navegador lo soporta; si no, selector de color nativo. */
  async pick(): Promise<void> {
    const EyeDropper = (window as any).EyeDropper;
    if (!EyeDropper) { this.openPicker(); return; }
    try {
      const res = await new EyeDropper().open();
      if (res?.sRGBHex) this.set(res.sRGBHex);
    } catch { /* cancelado por el usuario */ }
  }

  writeValue(v: string): void {
    const hex = isHex(normalizeHex(v || '')) ? normalizeHex(v) : '#FFFFFF';
    this.value.set(hex);
    this.draft.set(hex);
  }
  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
}
